import {
  trelloBoardCaptureSchema,
  trelloActionSchema,
  trelloBoardSchema,
  trelloBoardMembershipSchema,
  trelloCardSchema,
  trelloChecklistSchema,
  trelloCustomFieldSchema,
  trelloIdSchema,
  trelloLabelSchema,
  trelloListSchema,
  trelloMemberSchema,
  type TrelloBoardCapture,
} from "@board-preserve/contracts";

const TRELLO_API_ORIGIN = "https://api.trello.com/1/";
const DEFAULT_PAGE_SIZE = 1_000;
const MAX_PAGES = 10_000;

interface Parser<T> {
  parse(value: unknown): T;
}

export interface TrelloLocalExportSession {
  apiKey: string;
  boardId: string;
  token: string;
}

export type TrelloReadStage =
  | "actions"
  | "board"
  | "cards"
  | "checklists"
  | "custom_fields"
  | "labels"
  | "lists"
  | "members"
  | "memberships";

export interface TrelloReadProgress {
  itemCount: number;
  stage: TrelloReadStage;
}

export interface ReadTrelloBoardOptions {
  fetch?: typeof fetch;
  onProgress?: (progress: TrelloReadProgress) => void;
  pageSize?: number;
  signal?: AbortSignal;
}

export type TrelloReadErrorCode =
  | "authorization_required"
  | "invalid_response"
  | "rate_limited"
  | "request_failed";

export class TrelloReadError extends Error {
  readonly code: TrelloReadErrorCode;
  readonly retryAfterSeconds: number | null;

  constructor(
    code: TrelloReadErrorCode,
    message: string,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "TrelloReadError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function createRequestUrl(
  path: string,
  session: TrelloLocalExportSession,
  parameters: Readonly<Record<string, string>>,
): URL {
  const url = new URL(path, TRELLO_API_ORIGIN);

  for (const [key, value] of Object.entries(parameters)) {
    url.searchParams.set(key, value);
  }

  url.searchParams.set("key", session.apiKey);
  url.searchParams.set("token", session.token);
  return url;
}

function parseRetryAfter(value: string | null): number | null {
  if (value === null) {
    return null;
  }

  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

async function requestJson<T>(
  path: string,
  parameters: Readonly<Record<string, string>>,
  parser: Parser<T>,
  session: TrelloLocalExportSession,
  fetchImplementation: typeof fetch,
  signal: AbortSignal | undefined,
): Promise<T> {
  let response: Response;

  try {
    response = await fetchImplementation(
      createRequestUrl(path, session, parameters),
      {
        cache: "no-store",
        credentials: "omit",
        headers: { Accept: "application/json" },
        referrerPolicy: "no-referrer",
        ...(signal === undefined ? {} : { signal }),
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }

    throw new TrelloReadError(
      "request_failed",
      "Trello could not be reached. Check the connection and try again.",
    );
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new TrelloReadError(
        "authorization_required",
        "Trello authorization is missing, expired, or does not include the required access.",
      );
    }

    if (response.status === 429) {
      throw new TrelloReadError(
        "rate_limited",
        "Trello is rate limiting this export. Wait briefly and try again.",
        parseRetryAfter(response.headers.get("Retry-After")),
      );
    }

    throw new TrelloReadError(
      "request_failed",
      `Trello returned an unexpected HTTP ${response.status} response.`,
    );
  }

  let payload: unknown;
  try {
    payload = (await response.json()) as unknown;
  } catch {
    throw new TrelloReadError(
      "invalid_response",
      "Trello returned a response that was not valid JSON.",
    );
  }

  try {
    return parser.parse(payload);
  } catch {
    throw new TrelloReadError(
      "invalid_response",
      "Trello returned data in an unexpected shape. No archive was created.",
    );
  }
}

async function readPaginatedCollection<T extends { id: string }>(
  path: string,
  parameters: Readonly<Record<string, string>>,
  itemParser: Parser<T>,
  session: TrelloLocalExportSession,
  fetchImplementation: typeof fetch,
  signal: AbortSignal | undefined,
  pageSize: number,
  onPage?: (itemCount: number) => void,
): Promise<T[]> {
  const parser: Parser<T[]> = {
    parse: (value) => {
      if (!Array.isArray(value)) {
        throw new TypeError("Expected a Trello collection.");
      }

      return value.map((item) => itemParser.parse(item));
    },
  };
  const itemsById = new Map<string, T>();
  let before: string | undefined;

  for (let pageNumber = 0; pageNumber < MAX_PAGES; pageNumber += 1) {
    const page = await requestJson(
      path,
      {
        ...parameters,
        ...(before === undefined ? {} : { before }),
        limit: String(pageSize),
      },
      parser,
      session,
      fetchImplementation,
      signal,
    );

    for (const item of page) {
      itemsById.set(item.id, item);
    }
    onPage?.(itemsById.size);

    if (page.length < pageSize) {
      return [...itemsById.values()];
    }

    const nextBefore = page.at(-1)?.id;
    if (nextBefore === undefined || nextBefore === before) {
      throw new TrelloReadError(
        "invalid_response",
        "Trello pagination stopped advancing. No archive was created.",
      );
    }
    before = nextBefore;
  }

  throw new TrelloReadError(
    "invalid_response",
    "Trello pagination exceeded the safety limit. No archive was created.",
  );
}

function collectionParser<T>(itemParser: Parser<T>): Parser<T[]> {
  return {
    parse(value) {
      if (!Array.isArray(value)) {
        throw new TypeError("Expected a Trello collection.");
      }

      return value.map((item) => itemParser.parse(item));
    },
  };
}

export async function readTrelloBoard(
  sessionInput: TrelloLocalExportSession,
  options: ReadTrelloBoardOptions = {},
): Promise<TrelloBoardCapture> {
  const session = {
    apiKey: sessionInput.apiKey.trim(),
    boardId: trelloIdSchema.parse(sessionInput.boardId),
    token: sessionInput.token.trim(),
  };
  if (session.apiKey === "" || session.token === "") {
    throw new TrelloReadError(
      "authorization_required",
      "Trello authorization is required before reading this board.",
    );
  }

  const fetchImplementation = options.fetch ?? fetch;
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 1_000) {
    throw new RangeError("Trello page size must be between 1 and 1,000.");
  }

  const path = `boards/${encodeURIComponent(session.boardId)}`;
  const emit = (stage: TrelloReadStage, itemCount: number) => {
    options.onProgress?.({ itemCount, stage });
  };

  const board = await requestJson(
    path,
    {
      fields:
        "id,name,desc,closed,idOrganization,prefs,dateLastActivity,shortLink,shortUrl,subscribed,url",
    },
    trelloBoardSchema,
    session,
    fetchImplementation,
    options.signal,
  );
  emit("board", 1);

  const lists = await requestJson(
    `${path}/lists/all`,
    { fields: "id,idBoard,name,closed,pos" },
    collectionParser(trelloListSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("lists", lists.length);

  const labels = await requestJson(
    `${path}/labels`,
    { fields: "id,idBoard,name,color", limit: "1000" },
    collectionParser(trelloLabelSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("labels", labels.length);

  const members = await requestJson(
    `${path}/members`,
    { fields: "id,username,fullName" },
    collectionParser(trelloMemberSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("members", members.length);

  const memberships = await requestJson(
    `${path}/memberships`,
    { filter: "all" },
    collectionParser(trelloBoardMembershipSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("memberships", memberships.length);

  const cards = await readPaginatedCollection(
    `${path}/cards/all`,
    {
      attachment_fields: "id,name,fileName,url,isUpload,mimeType,bytes,date",
      attachments: "true",
      customFieldItems: "true",
      fields:
        "id,idBoard,idList,name,desc,closed,pos,start,due,dueComplete,dueReminder,dateLastActivity,cover,idAttachmentCover,idMembers,idMembersVoted,idLabels,idChecklists,idShort,manualCoverAttachment,shortLink,shortUrl,subscribed,url",
    },
    trelloCardSchema,
    session,
    fetchImplementation,
    options.signal,
    pageSize,
    (itemCount) => {
      emit("cards", itemCount);
    },
  );

  const checklists = await requestJson(
    `${path}/checklists`,
    {
      checkItem_fields: "id,name,state,pos,due,idMember",
      checkItems: "all",
      fields: "id,idBoard,idCard,name,pos",
    },
    collectionParser(trelloChecklistSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("checklists", checklists.length);

  const customFields = await requestJson(
    `${path}/customFields`,
    {},
    collectionParser(trelloCustomFieldSchema),
    session,
    fetchImplementation,
    options.signal,
  );
  emit("custom_fields", customFields.length);

  const boardActions = await readPaginatedCollection(
    `${path}/actions`,
    {
      fields: "id,idMemberCreator,type,date,data,display",
      filter: "all",
      memberCreator: "true",
      memberCreator_fields: "id,username,fullName",
    },
    trelloActionSchema,
    session,
    fetchImplementation,
    options.signal,
    pageSize,
    (itemCount) => {
      emit("actions", itemCount);
    },
  );
  const actions = boardActions.filter(
    (action) => action.data.card !== undefined,
  );
  emit("actions", actions.length);

  return trelloBoardCaptureSchema.parse({
    actions,
    board,
    cards,
    checklists,
    customFields,
    labels,
    lists,
    members,
    memberships,
  });
}
